import { GraduationCap } from 'lucide-react-native';
import { StyleSheet, View } from 'react-native';

import { HighlightedText } from '@/components/HighlightedText';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { stripHighlightMarkup } from '@/lib/highlightMarkup';

interface LandingCalloutProps {
  /** Rendered uppercase, e.g. "Student? Get **$50 total**". */
  title: string;
  body: string;
}

/**
 * Tinted upsell card on the public signup landing, driven by
 * Deal#marketing_copy.landing.callout ("Student? Get $50 total" for SoFi
 * Checking). Same tint family as the inviter chip; the icon ring echoes
 * the brand-logo ring above it. `**x**` in title/body renders accent green.
 */
export function LandingCallout({ title, body }: LandingCalloutProps) {
  return (
    <View
      style={styles.callout}
      accessibilityRole="summary"
      accessibilityLabel={`${stripHighlightMarkup(title)}. ${stripHighlightMarkup(body)}`}
    >
      <View style={styles.iconRing}>
        <GraduationCap size={24} color={colors.accentText} strokeWidth={2} />
      </View>
      <View style={styles.body}>
        <HighlightedText style={styles.title}>{title.toUpperCase()}</HighlightedText>
        <HighlightedText style={styles.text}>{body}</HighlightedText>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  callout: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.paidPillBg,
    borderWidth: 1,
    borderColor: colors.paidPillBorder,
    borderRadius: radii.base,
  },
  iconRing: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: colors.electricGreen,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: spacing.xs,
  },
  // sm (not base) so "STUDENT? GET $50 TOTAL" stays on one line beside the
  // 48px ring inside a 375pt-phone card; uppercase + bold carry the hierarchy.
  title: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.sm,
    color: colors.deepGreen,
    letterSpacing: 0.3,
  },
  text: {
    fontFamily: fonts.body,
    fontSize: fontSizes.base,
    color: colors.text,
    lineHeight: 22,
  },
});
